import { useEffect, useState } from "react";
import moment from "moment";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Skeleton,
  SxProps,
  Theme,
  Typography,
} from "@mui/material";
import { SystemStyleObject } from "@mui/system";
import { CustomInput } from "global/components";
import FeatureGateBlock from "global/components/FeatureGateBlock/FeatureGateBlock";
import { handleErrorMessage, openSuccessNotification } from "helpers/methods";
import { WorkspaceQuota } from "types/workspace";
import { notification, signer } from "theme/colors";
import {
  fetchWorkspaceQuota,
  removeWorkspaceQuota,
  saveWorkspaceQuota,
} from "./WorkspaceQuotaServices";
import {
  buttonRowSx,
  dangerDescriptionSx,
  dangerTitleSx,
  formFieldsSx,
  rowTextSx,
  sectionDescriptionSx,
  sectionDividerSx,
} from "./Workspaces.styles";

interface Props {
  workspaceId: string;
  /** Account admins set and remove quotas; everyone else who can see the section only reads it. */
  isAdmin: boolean;
  /** An archived workspace's quota can be seen but not changed. */
  archived: boolean;
}

/** Upper bound for the input, so a typo like 10000000 is caught before it is saved. */
const MAX_QUOTA = 1000000;
const ALERT_PERCENT = 80;
const REMOVE_DIALOG_TITLE_ID = "workspace-quota-remove-title";

const formatCount = (n?: number) => (n ?? 0).toLocaleString();
const envelopes = (n?: number) => `${formatCount(n)} envelope${n === 1 ? "" : "s"}`;
const formatDay = (day: moment.Moment) => day.format("MMM D, YYYY");

/** "Oct 1 – Oct 30, 2026", or "Dec 1, 2026 – Jan 30, 2027" when the period spans two years. */
const formatRange = (start?: string, end?: string) => {
  if (!start || !end) return "";
  const s = moment(start);
  const e = moment(end);
  return s.year() === e.year()
    ? `${s.format("MMM D")} – ${formatDay(e)}`
    : `${formatDay(s)} – ${formatDay(e)}`;
};

/** Returns what is wrong with the typed quota, or "" when it is a valid number. */
const validateQuota = (value: string): string => {
  const text = value.trim();
  if (text === "") return "Please enter a number of envelopes.";
  if (!/^\d+$/.test(text)) return "Please use whole numbers only, for example 100.";
  const n = Number(text);
  if (n < 1) return "The quota needs to be at least 1.";
  if (n > MAX_QUOTA) return `Please enter ${formatCount(MAX_QUOTA)} or less.`;
  return "";
};

// The same divider as the other blocks of the workspace details card.
const sectionSx = sectionDividerSx;
// Fixed widths so a label change ("Saving...") never shifts the buttons next to it.
const primaryButtonSx = { minWidth: 128 };
/**
 * A shared card style plus a few local properties. The shared styles are plain style objects typed
 * as `SxProps`, which an `sx` array does not accept as an item, so they are merged into one object.
 */
const withSx = (base: SxProps<Theme>, extra: SystemStyleObject<Theme>): SxProps<Theme> => ({
  ...(base as SystemStyleObject<Theme>),
  ...extra,
});
// The card's button row, with the buttons centred against each other.
const actionRowSx = withSx(buttonRowSx, { alignItems: "center" });
// The quota's own lines (usage, period, notes) use the card's description text.
const noteSx = sectionDescriptionSx;
const METER_HEIGHT = "6px";
const meterTrackSx = {
  height: METER_HEIGHT,
  borderRadius: "999px",
  bgcolor: signer.border.divider,
  overflow: "hidden",
};
const meterFillSx = (percent: number, full: boolean) => ({
  width: `${percent}%`,
  height: METER_HEIGHT,
  borderRadius: "999px",
  bgcolor: full ? notification.error.icon : signer.orange.main,
});
// Destructive text action, in the error red rather than the theme's ink.
const dangerTextSx = { color: notification.error.icon };

// A block heading of the details card, like "Archive workspace" (the card's h2 is the workspace name).
const Heading = () => (
  <Typography component="h3" sx={dangerTitleSx}>
    Envelope quota
  </Typography>
);

/** Same shape as the loaded section, so the page does not jump when the numbers arrive. */
export const QuotaSkeleton = () => (
  <Box sx={sectionSx} aria-busy="true" aria-label="Loading envelope quota">
    <Heading />
    <Skeleton variant="text" width="90%" height={18} />
    <Skeleton variant="text" width="60%" height={18} sx={{ mb: 2 }} />
    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
      <Skeleton variant="text" width={180} height={22} />
      <Skeleton variant="text" width={60} height={20} />
    </Box>
    <Skeleton variant="rounded" width="100%" height={6} sx={{ my: 1 }} />
    <Skeleton variant="text" width={260} height={18} sx={{ mb: 2 }} />
    <Box sx={formFieldsSx}>
      <Skeleton variant="text" width={160} height={18} />
      <Skeleton variant="rounded" height={44} />
      {/* blank like the real helper line */}
      <Box sx={{ height: 20 }} />
      <Box sx={actionRowSx}>
        <Skeleton variant="rounded" width={128} height={40} />
        <Skeleton variant="rounded" width={80} height={40} />
      </Box>
    </Box>
  </Box>
);

/**
 * Envelope quota for one workspace: "used of quota" for the current billing period, with the
 * period dates. The quota is a hard limit -- sends from the workspace stop when it is used up.
 */
const WorkspaceQuotaSection = ({ workspaceId, isAdmin, archived }: Props) => {
  const [quota, setQuota] = useState<WorkspaceQuota | null>(null);
  const [input, setInput] = useState<string>("");
  const [touched, setTouched] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadFailed, setLoadFailed] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [confirmRemove, setConfirmRemove] = useState<boolean>(false);

  const showQuota = (data: WorkspaceQuota | null) => {
    setQuota(data);
    setInput(typeof data?.quota === "number" ? String(data.quota) : "");
    setTouched(false);
  };

  const load = async (isCurrent: () => boolean = () => true) => {
    try {
      setLoading(true);
      setLoadFailed(false);
      const data = await fetchWorkspaceQuota(workspaceId);
      if (isCurrent()) showQuota(data ?? null);
    } catch (error: any) {
      if (!isCurrent()) return;
      setLoadFailed(true);
      handleErrorMessage(error);
    } finally {
      if (isCurrent()) setLoading(false);
    }
  };

  useEffect(() => {
    // Ignore a slow answer for a workspace the user has already left.
    let current = true;
    load(() => current);
    return () => {
      current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  if (loading && !quota) return <QuotaSkeleton />;

  if (loadFailed && !quota) {
    return (
      <Box sx={sectionSx}>
        <Heading />
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => load()}>
              Try again
            </Button>
          }
        >
          We couldn't load the envelope quota right now. Please try again.
        </Alert>
      </Box>
    );
  }
  if (!quota) return null;

  if (!quota.eligible) {
    return (
      <Box sx={sectionSx}>
        <Heading />
        <FeatureGateBlock status="not-in-plan" variant="inline" />
      </Box>
    );
  }

  const canEdit = isAdmin && !archived;
  const hasQuota = typeof quota.quota === "number";
  const full = hasQuota && (quota.remaining ?? 0) <= 0;
  const percent = Math.min(100, Math.max(0, quota.percentUsed ?? 0));
  const nearlyFull = hasQuota && !full && percent >= ALERT_PERCENT;
  // periodEnd is the period's last day; sending starts again the day after.
  const resetsOn = quota.periodEnd ? formatDay(moment(quota.periodEnd).add(1, "day")) : "";

  const error = validateQuota(input);
  const inputValid = error === "";
  const parsed = inputValid ? Number(input.trim()) : NaN;
  const dirty = input.trim() !== (hasQuota ? String(quota.quota) : "");
  const canSave = inputValid && dirty;
  // Show an error while a wrong value is typed, or once the field is left empty.
  const showError = !inputValid && (touched || input.trim() !== "");

  // A saved quota above the plan limit already shows the backend's warning; these are for a new value.
  const overPlan =
    canSave && typeof quota.planLimit === "number" && parsed > quota.planLimit;
  const belowUsed = canSave && hasQuota && parsed <= quota.used;

  const save = async () => {
    setTouched(true);
    if (!canSave || submitting) return;
    try {
      setSubmitting(true);
      const saved = await saveWorkspaceQuota(workspaceId, parsed);
      showQuota(saved ?? null);
      openSuccessNotification(
        hasQuota
          ? `Quota updated. This workspace can now send up to ${envelopes(parsed)} per billing period.`
          : `Quota set. This workspace can now send up to ${envelopes(parsed)} per billing period.`,
      );
    } catch (err: any) {
      handleErrorMessage(err);
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async () => {
    try {
      setSubmitting(true);
      await removeWorkspaceQuota(workspaceId);
      setConfirmRemove(false);
      // Show "no quota" right away, so a failed refresh below never leaves the removed quota on screen.
      showQuota({
        ...quota,
        quota: undefined,
        remaining: undefined,
        percentUsed: undefined,
        warning: undefined,
        used: 0,
      });
      openSuccessNotification("Quota removed. This workspace now follows your plan's limit.");
      await load();
    } catch (err: any) {
      handleErrorMessage(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={sectionSx}>
      <Heading />
      <Typography component="div" sx={dangerDescriptionSx}>
        Choose how many envelopes this workspace can send in each billing period. When
        it reaches the limit, sending pauses until the next period starts. Drafts
        don't count, but envelopes you void or delete still do.
      </Typography>

      {hasQuota ? (
        <Box sx={{ mb: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "baseline",
              gap: 1,
            }}
          >
            <Typography component="div" sx={rowTextSx("ink", "strong")}>
              {formatCount(quota.used)} of {envelopes(quota.quota)} used
            </Typography>
            <Typography
              component="div"
              sx={withSx(rowTextSx("body", "regular"), full ? dangerTextSx : {})}
            >
              {full ? "None left" : `${formatCount(quota.remaining)} left`}
            </Typography>
          </Box>
          {/* Same meter as the Team page's seats card: orange fill, red only once it's full. */}
          <Box
            role="progressbar"
            aria-valuenow={percent}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`${percent}% of the envelope quota used`}
            sx={{ ...meterTrackSx, my: 1 }}
          >
            <Box sx={meterFillSx(percent, full)} />
          </Box>
          <Typography component="div" sx={noteSx}>
            {resetsOn && `Resets on ${resetsOn} · `}This period:{" "}
            {formatRange(quota.periodStart, quota.periodEnd)}
          </Typography>
          {full && (
            <Alert severity="error" sx={{ mt: 1.5 }}>
              This workspace has used all of its envelopes for this period.
              {resetsOn ? ` Sending will start again on ${resetsOn}.` : ""}
              {canEdit ? " To send sooner, raise the quota below." : ""}
            </Alert>
          )}
          {nearlyFull && (
            <Alert severity="warning" sx={{ mt: 1.5 }}>
              Heads up: this workspace has used {percent}% of its envelopes for this
              period.
            </Alert>
          )}
          {quota.warning && (
            <Alert severity="warning" sx={{ mt: 1.5 }}>
              {quota.warning}
            </Alert>
          )}
        </Box>
      ) : (
        <Typography component="div" sx={withSx(noteSx, { mb: 2 })}>
          There's no quota on this workspace yet, so it can send as many envelopes as
          your plan allows.
        </Typography>
      )}

      {!canEdit && (
        <Typography component="div" sx={noteSx}>
          {archived
            ? "This workspace is archived. Restore it to change its quota."
            : "Only account admins can change this quota."}
        </Typography>
      )}

      {canEdit && (
        <Box
          component="form"
          noValidate
          onSubmit={(e: React.FormEvent) => {
            e.preventDefault();
            save();
          }}
          sx={formFieldsSx}
        >
          {/*
            The card's field, as wide as the name field above. No hint text, but the helper
            line keeps its height (" ") so an error never moves the buttons. CustomInput lets
            helperText override its error text, so the message goes in both.
          */}
          <CustomInput
            id="workspaceQuota"
            name="workspaceQuota"
            label="Envelopes per billing period"
            placeHolder="For example, 100"
            value={input}
            onChange={(e: any) => setInput(e.target.value.replace(/\s/g, ""))}
            onBlur={() => setTouched(true)}
            disabled={submitting}
            propsToInputElement={{ inputMode: "numeric", pattern: "[0-9]*", maxLength: 7 }}
            error={showError ? error : null}
            helperText={showError ? error : " "}
          />
          <Box sx={actionRowSx}>
            <Button
              type="submit"
              variant="contained"
              disabled={submitting || !canSave}
              sx={primaryButtonSx}
            >
              {submitting ? "Saving..." : hasQuota ? "Update quota" : "Set quota"}
            </Button>
            {/* Always shown, enabled once something has been typed, so the row keeps its shape. */}
            <Button onClick={() => showQuota(quota)} disabled={submitting || !dirty}>
              Cancel
            </Button>
            {hasQuota && (
              <Button
                onClick={() => setConfirmRemove(true)}
                disabled={submitting}
                sx={{ ...dangerTextSx, ml: "auto" }}
              >
                Remove quota
              </Button>
            )}
          </Box>
          {/* Notes about the typed value go below the buttons, so they never push them around. */}
          {belowUsed && (
            <Alert severity="warning">
              This workspace has already sent {envelopes(quota.used)} this period. With a
              quota of {formatCount(parsed)}, it won't be able to send any more
              {resetsOn ? ` until ${resetsOn}` : " until the next period"}.
            </Alert>
          )}
          {overPlan && (
            <Alert severity="info">
              Your plan includes {envelopes(quota.planLimit)}, so your plan's limit will
              be reached before this quota.
            </Alert>
          )}
        </Box>
      )}

      <Dialog
        open={confirmRemove}
        onClose={() => !submitting && setConfirmRemove(false)}
        maxWidth="xs"
        fullWidth
        aria-labelledby={REMOVE_DIALOG_TITLE_ID}
      >
        <DialogTitle id={REMOVE_DIALOG_TITLE_ID}>Remove this quota?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This workspace will be able to send as many envelopes as your plan allows.
            Its usage count will also be cleared, so if you add a quota again later,
            counting starts from zero.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRemove(false)} disabled={submitting}>
            Keep quota
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={remove}
            disabled={submitting}
            sx={primaryButtonSx}
          >
            {submitting ? "Removing..." : "Remove quota"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WorkspaceQuotaSection;
